import { Clear } from '@mui/icons-material';
import { Box, Button, Typography } from '@mui/material';
import { useEffect, useRef } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { List, ListImperativeAPI } from 'react-window';

import Search from '@uiKit/SearchInput';
import { useCardScanner } from 'src/hooks/useCardScanner';
import { ReviewItemsFilterType } from 'src/types/shared';
import { DebounceTextFieldHandle } from 'src/uiKit/DebounceTextField';

import { CaptureId, CapturePiece } from '../../../../types';
import ListItem from './Item';
import { sxColumnFlex, sxItemsContainer } from './styles';

type Props = {
  selected: CaptureId;
  onClear: () => void;
  filter: ReviewItemsFilterType | null;
  onItemClick: (key: CaptureId | null) => void;
  items: CapturePiece[];
  setSearch: (value: string) => void;
};

const FormElementTagNames = ['INPUT', 'TEXTAREA'];

const ItemsList = ({ selected, items, setSearch, onItemClick, filter, onClear }: Props) => {
  const listRef = useRef<ListImperativeAPI>(null);
  const searchInputRef = useRef<{ setValue: (value: string) => void } | null>(null);
  const isBarcodeEvent = useRef(false);

  // select first item if input was barcode
  useEffect(() => {
    if(isBarcodeEvent.current) {
      if (items[0]) {
        if (selected !== items[0].id) {
          onItemClick(items[0].id);
        }
      } else {
        onItemClick(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const searchRef = useRef<DebounceTextFieldHandle>({
    setValue: (value: string) => {
      setSearch(value);
      searchInputRef.current?.setValue(value);
      isBarcodeEvent.current = true;
    },
    resetDebouncedTextFieldValue: () => {
      setSearch('');
      searchInputRef.current?.setValue('');
    },
  });

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight', 'Space'].includes(event.code)) return;

      const target = event.target as HTMLElement;
      if (FormElementTagNames.includes(target.tagName)) {
        return;
      }
      event.preventDefault();
      const index = items.findIndex(({ id }) => id === selected);
      if (['ArrowRight', 'ArrowDown', 'Space'].includes(event.code)) {
        const nextIndex = index + 1;
        if (items[nextIndex]?.id) {
          onItemClick(items[nextIndex].id);
          listRef?.current?.scrollToRow({ align: 'smart', behavior: 'smooth', index: nextIndex });
        }
      }
      if (['ArrowUp', 'ArrowLeft'].includes(event.code)) {
        const prevIndex = index - 1;
        if (prevIndex > -1 && items[prevIndex]?.id) {
          onItemClick(items[prevIndex].id);
          listRef?.current?.scrollToRow({ align: 'smart', behavior: 'smooth', index: prevIndex });
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [items, selected, onItemClick, listRef]);

  useCardScanner({ searchRef });

  return (
    <Box sx={sxItemsContainer}>
      <Box sx={{ mx: 2.5, mb: 2.5 }}>
        <Search
          setSearchResult={(value) => {
            setSearch(value);
            isBarcodeEvent.current = false;
          }}
          debounce={false}
          ref={searchInputRef}
        />
        {filter ? (
          <Typography variant="body2" sx={{ mt: 2, display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            {items.length}&nbsp;<Typography color="text.secondary">{filter}</Typography>&nbsp;
            <Button variant="text" color="error" onClick={onClear} startIcon={<Clear />}>
              Clear
            </Button>
          </Typography>
        ) : null}
      </Box>

      <Box sx={sxColumnFlex}>
        <AutoSizer disableWidth>
          {({ height }) => (
            <List
              rowHeight={88}
              rowCount={items.length}
              rowComponent={ListItem}
              className="customScrollBar"
              listRef={listRef}
              rowProps={{
                items,
                onClick: onItemClick,
                selected,
              }}
              style={{ height, maxHeight: 'inherit' }}
            />
          )}
        </AutoSizer>
      </Box>
    </Box>
  );
};

export default ItemsList;
